import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BuildingIcon, FileIcon, FilesIcon } from 'lucide-react';
import { overlay } from 'overlay-kit';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { MAX_STEP } from '@/entities/cmo-record/model/constants';
import {
  type CreateRecordRequest,
  createRecordRequestSchema,
  type ManufactureCMOInfo,
  type PackageCMOInfo,
  type PreparationResponse,
  RECORD_TYPE,
} from '@/entities/cmo-record/model/types';
import { api, getErrorMessage } from '@/shared/api/api';
import { Button } from '@/shared/ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Text } from '@/shared/ui/text';
import { formatFileSize } from '@/shared/utils';
import { useCMORecords } from '../model/context';
import { DataTable } from '../model/data-table';
import { useCMOTable } from '../model/use-cmo-table';

interface Step2Props {
  step: number;
}

export function Step2({ step }: Step2Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const dkDocumentId = searchParams.get('dk_document_id');
  const { uploadedFile, recordType } = useCMORecords();
  const [selectedRows, setSelectedRows] = useState<(ManufactureCMOInfo | PackageCMOInfo)[]>([]);

  const { mutate: createRecord, isPending: isCreatingRecord } = useMutation({
    mutationFn: async ({ recordType, dkDocumentId, fields }: CreateRecordRequest) => {
      // 1. Revision 업데이트
      await api.patch('contractor-records/revision-no', {
        master_data_type: recordType,
        updates: fields,
      });

      // 2. 기록서 생성
      return await api.post('contractor-records', {
        document_id: Number(dkDocumentId),
        master_data_type: recordType,
        ids: fields.map((field) => field.id),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['cmo-records', recordType] });
      toast.success('기록서 생성이 완료되었습니다.');
      navigate(`/records`);
    },
    onError: async (error) => {
      const errorMessage = await getErrorMessage(error);
      overlay.open(({ isOpen, close }) => (
        <Dialog
          onOpenChange={close}
          open={isOpen}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>기록서 생성 오류</DialogTitle>
              <DialogDescription>{errorMessage}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                onClick={() => close()}
                variant={'primary'}
              >
                닫기
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ));
    },
  });

  useEffect(() => {
    const checkStep = async () => {
      if (step > MAX_STEP || step !== 2 || Number.isNaN(step)) {
        const response = await overlay.openAsync(({ isOpen, close }) => (
          <Dialog
            onOpenChange={close}
            open={isOpen}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>잘못된 접근</DialogTitle>
                <DialogDescription>유효하지 않은 단계입니다.</DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  onClick={() => close(true)}
                  variant={'primary'}
                >
                  닫기
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        ));

        if (response) {
          navigate('/cmo-records/1');
        }
      }
    };

    checkStep().then();
  }, [navigate, step]);

  // recordType에 따라 다른 엔드포인트 호출
  const preparationEndpoint =
    recordType === RECORD_TYPE.MANUFACTURE
      ? 'contractor-records/manufactures/preparation'
      : 'contractor-records/package/preparation';

  const {
    data: preparationData,
    isLoading: loading,
    isError: isPreparationError,
    error: preparationError,
  } = useQuery({
    queryKey: ['cmo-records-preparation', recordType, dkDocumentId],
    queryFn: async () => {
      return await api.get<PreparationResponse>(
        `${preparationEndpoint}?dk_document_id=${dkDocumentId}`
      );
    },
    enabled: !!dkDocumentId && !!recordType && !!uploadedFile,
    retry: false,
  });

  useEffect(() => {
    if (!isPreparationError) return;

    const showError = async () => {
      const errorMessage = await getErrorMessage(preparationError);
      const response = await overlay.openAsync(({ isOpen, close }) => (
        <Dialog
          onOpenChange={close}
          open={isOpen}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>안내</DialogTitle>
              <DialogDescription className="whitespace-pre-line">{errorMessage}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                onClick={() => close(true)}
                variant={'primary'}
              >
                확인
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ));

      if (response) {
        navigate('/cmo-records/1');
      }
    };

    showError().then();
  }, [isPreparationError, preparationError, navigate]);

  const { localData, manufacturingColumns, packagingColumns, setLocalData } =
    useCMOTable(preparationData);

  const handleRowSelectionChange = (rows: (ManufactureCMOInfo | PackageCMOInfo)[]) => {
    setSelectedRows(structuredClone(rows));
  };

  useEffect(() => {
    const validateUploadedFile = async () => {
      if (uploadedFile === null || dkDocumentId === null || Number.isNaN(Number(dkDocumentId))) {
        const response = await overlay.openAsync(({ isOpen, close }) => (
          <Dialog
            onOpenChange={close}
            open={isOpen}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>잘못된 접근</DialogTitle>
                <DialogDescription>
                  업로드된 파일이 없습니다. 이전 단계로 돌아가주세요.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  onClick={() => close(true)}
                  variant={'primary'}
                >
                  닫기
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        ));

        if (response) {
          navigate('/cmo-records/1');
        }
      }
    };

    validateUploadedFile().then();
  }, [dkDocumentId, navigate, uploadedFile]);

  const documentInfoList = useMemo(() => {
    return preparationData
      ? [
          {
            label: '총 페이지 수',
            icon: FilesIcon,
            value: 'total_pages' as const,
            render: (value: number) => (
              <>
                <Text
                  className="inline text-primary"
                  typography="typo-text-xl-bold"
                >
                  {value}
                </Text>{' '}
                <Text
                  className="inline"
                  typography="typo-text-xl"
                >
                  Page
                </Text>
              </>
            ),
          },
          {
            label: '작성 가능 수탁사',
            icon: BuildingIcon,
            value: 'total_contractor' as const,
            render: (value: number) => (
              <>
                <Text
                  className="inline text-primary"
                  typography="typo-text-xl-bold"
                >
                  {value}
                </Text>
                <Text
                  className="inline"
                  typography="typo-text-xl"
                >
                  개사
                </Text>
              </>
            ),
          },
        ]
      : [];
  }, [preparationData]);

  const handleCreateRecord = async () => {
    if (recordType === null || dkDocumentId === null) return;

    const selectedMainCodes = new Set(selectedRows.map((row) => row.main_code));
    const freshSelectedRows = localData.filter((row) => selectedMainCodes.has(row.main_code));

    const validatedFields = createRecordRequestSchema.safeParse({
      recordType,
      dkDocumentId,
      fields: freshSelectedRows.map((row) => ({
        revision_no: row.revision_no.trim(),
        id: row.id,
      })),
    });

    if (!validatedFields.success) {
      const response = await overlay.openAsync(({ isOpen, close }) => (
        <Dialog
          onOpenChange={close}
          open={isOpen}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>오류 안내</DialogTitle>
              <DialogDescription>
                개정번호는 필수 입력으로 빈값을 저장할 수 없습니다.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                onClick={() => close(true)}
                variant={'primary'}
              >
                닫기
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ));

      if (response) {
        // 빈 revision_no를 '01'로 초기화
        const updatedData = localData.map((row) => ({
          ...row,
          revision_no: row.revision_no.trim() === '' ? '01' : row.revision_no,
        }));
        setLocalData(updatedData);
      }

      return;
    }

    createRecord(validatedFields.data);
  };

  return (
    <div className="w-full h-full flex flex-col p-7 space-y-2">
      <header className="flex flex-col gap-2 shrink-0">
        <Text
          tag="h1"
          typography="typo-title-m-bold"
        >
          수탁사 기록서 만들기
        </Text>
        <Text
          className="text-muted-foreground"
          typography="typo-text-xl"
        >
          업로드된 문서로 수많은 문서를 생성해보세요.
        </Text>
      </header>
      <section className="w-full py-4 px-6 bg-white border shadow-sm rounded mt-6 space-y-3">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 text-muted-foreground">
            <FileIcon className="size-4" />
            <Text typography="typo-text-xl">원본 문서</Text>
          </div>
          <Text typography="typo-text-xl">
            {preparationData?.original_file_name}({formatFileSize(uploadedFile?.size ?? 0)})
          </Text>
        </div>

        <div className="flex-1 flex gap-4">
          {documentInfoList.map((info) => {
            const value = preparationData?.[info.value as keyof PreparationResponse] ?? 0;

            return (
              <div
                className="flex-1 flex flex-col gap-2"
                key={info.value}
              >
                <div className="flex items-center gap-2 text-muted-foreground">
                  <info.icon className="size-4" />
                  <Text typography="typo-text-xl">{info.label}</Text>
                </div>
                <div className="text-center py-3 bg-zinc-50 rounded">
                  {info.render(value as number)}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="flex-1 mt-6 gap-3 relative flex flex-col min-h-0">
        <Text
          className="flex items-center"
          typography="typo-text-xl"
        >
          <Text
            className="text-primary"
            typography="typo-text-xl-bold"
          >
            {recordType === RECORD_TYPE.MANUFACTURE ? '제조지시 기록서' : '포장지시 기록서'}
          </Text>
          &nbsp; 수탁사 목록
        </Text>

        {recordType === RECORD_TYPE.MANUFACTURE ? (
          <DataTable<ManufactureCMOInfo, unknown>
            columns={manufacturingColumns}
            data={localData as ManufactureCMOInfo[]}
            enablePagination={false}
            isLastPage
            isLoading={loading}
            onRowSelectionChange={handleRowSelectionChange}
          />
        ) : (
          <DataTable<PackageCMOInfo, unknown>
            columns={packagingColumns}
            data={localData as PackageCMOInfo[]}
            enablePagination={false}
            isLastPage
            isLoading={loading}
            onRowSelectionChange={handleRowSelectionChange}
          />
        )}
      </section>

      <div className="flex items-center justify-center gap-4 py-4">
        <div className="flex gap-2">
          <Button
            onClick={() => navigate('/cmo-records/1')}
            variant="secondary"
          >
            취소
          </Button>
          <Button
            disabled={selectedRows.length === 0}
            onClick={handleCreateRecord}
            pending={isCreatingRecord}
            variant="primary"
          >
            {selectedRows.length > 0 ? `${selectedRows.length}개 생성하기` : '항목을 선택해주세요'}
          </Button>
        </div>
      </div>
    </div>
  );
}
